import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Space,
  DatePicker,
  Alert,
  Row,
  Col,
  Divider,
  Typography,
  Segmented,
  Radio,
  Modal,
  Descriptions,
} from 'antd';
import { formatters } from '../../utils/formatters';
import {
  SendOutlined,
  PictureOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  ExperimentOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { useAppDispatch, useAppSelector } from 'store';
import {
  sendPush,
  fetchSegments,
  fetchTemplates,
  selectSegments,
  selectTemplates,
  selectPushSending,
} from 'store/slices/push.slice';
import {
  fetchApplicationsLookup,
  selectApplicationsLookup,
} from 'store/slices/applications.slice';
import { applicationsApi } from 'api/endpoints/applications.api';
import { pushApi } from 'api/endpoints/push.api';
import { SendPushRequest, PushType } from 'types/push.types';
import { PushTemplate } from 'types/push_management.types';
import { validators } from 'utils/validators';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import { resolve } from 'utils/resolve';
import {
  getStoredAppId,
  getStoredTestToken,
  setStoredTestToken,
} from 'utils/pushCenterStorage';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text: AntText } = Typography;

const PUSH_TYPE_LABELS: Record<string, string> = {
  [PushType.REGULAR]: 'Обычное',
  [PushType.EVENT]: 'Событийное',
  [PushType.SPECIAL]: 'Специальное',
};

const TIMEZONE_PRESETS = [
  'UTC',
  'Europe/Stockholm',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Lisbon',
  'Europe/Istanbul',
  'Europe/Moscow',
  'America/Sao_Paulo',
];

const buildScheduleISO = (value: Dayjs, tz: string): string =>
  dayjs.tz(value.format('YYYY-MM-DDTHH:mm:00'), tz).toISOString();

const pluralUsers = (n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'пользователь';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'пользователя';
  return 'пользователей';
};

const SendPush: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const appsLookup = useAppSelector(selectApplicationsLookup);
  const segments = useAppSelector(selectSegments);
  const templates = useAppSelector(selectTemplates);
  const isSending = useAppSelector(selectPushSending);
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [appsLoading, setAppsLoading] = useState<boolean>(true);
  const [targetType, setTargetType] = useState<'all' | 'segment'>('all');
  const [scheduleType, setScheduleType] = useState<'now' | 'later'>('now');
  const [useTemplate, setUseTemplate] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<PushTemplate | null>(null);
  const [imageError, setImageError] = useState<boolean>(false);
  const browserTz = useMemo(() => dayjs.tz.guess(), []);
  const [timezone, setTimezone] = useState<string>(browserTz);
  const [appReach, setAppReach] = useState<number>(0);
  const [reachLoading, setReachLoading] = useState<boolean>(false);
  const [segmentReach, setSegmentReach] = useState<number | null>(null);
  const [confirmValues, setConfirmValues] = useState<any | null>(null);
  const [testToken, setTestToken] = useState<string>(getStoredTestToken() ?? '');
  const [testSending, setTestSending] = useState<boolean>(false);
  const imageUrl = Form.useWatch('image_url', form);
  const scheduleAt = Form.useWatch('schedule_at', form);
  const estimatedReach = targetType === 'segment' ? segmentReach ?? 0 : appReach;
  const selectedApp = useMemo(
    () => appsLookup.find((a) => a.id === selectedAppId) ?? null,
    [appsLookup, selectedAppId]
  );
  const appOptions = useMemo(
    () =>
      appsLookup.map((app) => ({
        value: app.id,
        label: `${app.name} (${app.bundle_id})`,
      })),
    [appsLookup]
  );
  const timezoneOptions = useMemo(() => {
    const list = Array.from(new Set([browserTz, ...TIMEZONE_PRESETS]));
    return list.map((tz) => ({
      value: tz,
      label: `${tz} — ${dayjs().tz(tz).format('HH:mm')}${tz === browserTz ? ' (ваша)' : ''}`,
    }));
  }, [browserTz]);

  useEffect(() => {
    setAppsLoading(true);

    dispatch(fetchApplicationsLookup())
      .unwrap()
      .then((apps) => {
        const storedAppId = getStoredAppId();
        if (!storedAppId) return;
        if (!apps.some((a) => a.id === storedAppId)) return;
        setSelectedAppId(storedAppId);
        form.setFieldValue('app_id', storedAppId);
      })
      .catch(console.error)
      .finally(() => setAppsLoading(false));
  }, [dispatch, form]);

  useEffect(() => {
    if (!selectedAppId) {
      setAppReach(0);
      return;
    }

    dispatch(fetchSegments({ appId: selectedAppId }));
    dispatch(fetchTemplates(selectedAppId));

    let cancelled = false;
    setReachLoading(true);
    applicationsApi
      .getUserStats(selectedAppId)
      .then((stats) => {
        if (!cancelled) setAppReach(stats.active_users ?? 0);
      })
      .catch(() => {
        if (!cancelled) setAppReach(0);
      })
      .finally(() => {
        if (!cancelled) setReachLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAppId, dispatch]);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  useEffect(() => {
    if (scheduleType === 'later' && scheduleAt) {
      form.validateFields(['schedule_at']).catch(() => undefined);
    }
  }, [timezone]);

  const handleAppChange = (appId: number) => {
    setSelectedAppId(appId);
    form.setFieldsValue({ segment_id: undefined });
    setSegmentReach(null);
    if (useTemplate) {
      setUseTemplate(false);
      setSelectedTemplate(null);
    }
  };

  const handleSegmentChange = (segmentId: number) => {
    const segment = segments.find((s) => s.id === segmentId);
    setSegmentReach(segment ? segment.user_count : null);
  };

  const handleTemplateChange = (templateId: number) => {
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      form.setFieldsValue({
        title: template.title,
        body: template.body,
        image_url: resolve(template.image_url, ''),
        custom_data: template.custom_data
          ? JSON.stringify(template.custom_data, null, 2)
          : '',
      });
    }
  };

    const validatePushSize = (_rule: any, _value: any) => {
    const title = form.getFieldValue('title') || '';
    const body = form.getFieldValue('body') || '';
    const customData = form.getFieldValue('custom_data') || {};

    if (!validators.pushMessageSize(title, body, customData)) {
      return Promise.reject('Размер push-сообщения превышает 4KB');
    }
    return Promise.resolve();
  };

  const buildPayload = (values: any): SendPushRequest | null => {
    const payload: SendPushRequest = {
      app_id: values.app_id,
      title: values.title,
      body: values.body,
      push_type: values.push_type,
      image_url: values.image_url || undefined,
      click_url: values.click_url || undefined,
    };

    if (targetType === 'segment' && values.segment_id) {
      payload.segment_id = values.segment_id;
    }

    if (scheduleType === 'later' && values.schedule_at) {
      payload.schedule_at = buildScheduleISO(values.schedule_at, timezone);
    }

    if (values.custom_data) {
      try {
        payload.custom_data = JSON.parse(values.custom_data);
      } catch {
        notify.error('Неверный формат JSON в дополнительных данных');
        return null;
      }
    }

    return payload;
  };

  const handleFinish = (values: any) => {
    setConfirmValues(values);
  };

  const handleConfirmedSend = async () => {
    if (!confirmValues) return;

    const payload = buildPayload(confirmValues);
    if (!payload) {
      setConfirmValues(null);
      return;
    }

    try {
      await dispatch(sendPush(payload)).unwrap();
      setConfirmValues(null);
      notify.success(
        scheduleType === 'now'
          ? 'Push-уведомление отправлено'
          : 'Push-уведомление запланировано'
      );
      navigate(ROUTES.PUSH_CENTER);
    } catch (error: any) {
      notify.error(error.message || 'Ошибка отправки push-уведомления');
    }
  };

  const handleTestSend = async () => {
    const token = testToken.trim();
    if (!token) {
      notify.error('Введите push-токен устройства');
      return;
    }

    let values: any;
    try {
      values = await form.validateFields([
        'app_id',
        'push_type',
        'title',
        'body',
        'image_url',
        'click_url',
        'custom_data',
      ]);
    } catch {
      notify.error('Заполните приложение, заголовок и текст');
      return;
    }

    const payload = buildPayload(values);
    if (!payload) return;

    setTestSending(true);
    try {
      await pushApi.sendTest({
        app_id: payload.app_id,
        title: payload.title,
        body: payload.body,
        push_type: payload.push_type,
        image_url: payload.image_url,
        click_url: payload.click_url,
        custom_data: payload.custom_data,
        push_token: token,
      });
      setStoredTestToken(token);
      notify.success('Тестовый push отправлен на устройство');
    } catch (error: any) {
      notify.error(error.message || 'Не удалось отправить тестовый push');
    } finally {
      setTestSending(false);
    }
  };

  const confirmSegmentName = confirmValues?.segment_id
    ? segments.find((s) => s.id === confirmValues.segment_id)?.name
    : null;

  const confirmScheduleISO =
    scheduleType === 'later' && confirmValues?.schedule_at
      ? buildScheduleISO(confirmValues.schedule_at, timezone)
      : null;

  const reachMessage = !selectedAppId
    ? 'Предполагаемый охват: выберите приложение'
    : reachLoading && targetType === 'all'
    ? 'Предполагаемый охват: считаем…'
    : `Предполагаемый охват: ${formatters.number(estimatedReach)} ${pluralUsers(
        estimatedReach
      )}`;

  return (
    <div className="send-push-page">
      <Card>
        <div className="page-header">
          <Title level={2}>Разовая отправка Push</Title>
          <AntText type="secondary">
            Отправьте push-уведомление сейчас или запланируйте на определенное время
          </AntText>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          scrollToFirstError
          initialValues={{
            push_type: PushType.SPECIAL,
          }}
        >
          <Row gutter={24}>
            <Col xs={24} lg={16}>
              <Card title="Содержание уведомления" className="content-card">
                <Form.Item
                  name="app_id"
                  label="Приложение"
                  rules={[
                    { required: true, message: 'Выберите приложение' },
                  ]}
                >
                  <Select
                    placeholder="Выберите приложение"
                    onChange={handleAppChange}
                    showSearch
                    optionFilterProp="label"
                    loading={appsLoading}
                    options={appOptions}
                  />
                </Form.Item>
                <Form.Item
                  name="push_type"
                  label="Тип уведомления"
                  rules={[{ required: true }]}
                >
                  <Segmented
                    options={[
                      { label: 'Обычное', value: PushType.REGULAR },
                      { label: 'Событийное', value: PushType.EVENT },
                      { label: 'Специальное', value: PushType.SPECIAL },
                    ]}
                    block
                  />
                </Form.Item>
                <Divider />
                <Form.Item label="Контент">
                  <Radio.Group
                    value={useTemplate}
                    onChange={(e) => {
                      setUseTemplate(e.target.value);
                      if (!e.target.value) {
                        setSelectedTemplate(null);
                        form.setFieldsValue({
                          title: '',
                          body: '',
                          image_url: '',
                          custom_data: '',
                        });
                      }
                    }}
                  >
                    <Radio value={false}>Создать вручную</Radio>
                    <Radio value={true} disabled={!selectedAppId || templates.length === 0}>
                      Использовать шаблон
                      {templates.length === 0 && selectedAppId && (
                        <AntText type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                          (нет шаблонов)
                        </AntText>
                      )}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
                {useTemplate && (
                  <>
                    <Form.Item label="Шаблон">
                      <Select
                        placeholder="Выберите шаблон"
                        onChange={handleTemplateChange}
                        value={selectedTemplate?.id}
                        allowClear
                        onClear={() => {
                          setSelectedTemplate(null);
                          form.setFieldsValue({
                            title: '',
                            body: '',
                            image_url: '',
                            custom_data: '',
                          });
                        }}
                      >
                        {templates.map((template) => (
                          <Option key={template.id} value={template.id}>
                            <div>
                              <div style={{ fontWeight: 500 }}>{template.name}</div>
                              {resolve(template.description) && (
                                <AntText type="secondary" style={{ fontSize: 12 }}>
                                  {resolve(template.description, '')}
                                </AntText>
                              )}
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    {selectedTemplate && (
                      <Alert
                        message="Вы можете отредактировать содержимое шаблона перед отправкой"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    )}
                  </>
                )}
                <Form.Item
                  name="title"
                  label="Заголовок"
                  rules={[
                    { required: true, message: 'Введите заголовок' },
                    { max: 100, message: 'Максимум 100 символов' },
                    { validator: validatePushSize },
                  ]}
                >
                  <Input
                    placeholder="Введите заголовок уведомления"
                    maxLength={100}
                    showCount
                  />
                </Form.Item>
                <Form.Item
                  name="body"
                  label="Текст сообщения"
                  rules={[
                    { required: true, message: 'Введите текст' },
                    { max: 500, message: 'Максимум 500 символов' },
                    { validator: validatePushSize },
                  ]}
                >
                  <TextArea
                    placeholder="Введите текст уведомления"
                    rows={4}
                    maxLength={500}
                    showCount
                  />
                </Form.Item>

                <Divider />
                <Form.Item
                  name="image_url"
                  label="Изображение (необязательно)"
                  rules={[
                    { type: 'url', message: 'Введите корректный URL (http:// или https://)' },
                  ]}
                  extra="Прямая публичная ссылка на картинку. FCM не принимает data-URL и ссылки, закрытые авторизацией."
                >
                  <Input
                    placeholder="https://example.com/banner.jpg"
                    prefix={<PictureOutlined />}
                    allowClear
                  />
                </Form.Item>

                {imageUrl && (
                  <div style={{ marginBottom: 24 }}>
                    {imageError ? (
                      <Alert
                        type="warning"
                        showIcon
                        message="Изображение по этой ссылке не загружается — проверьте URL"
                      />
                    ) : (
                      <div className="image-preview">
                        <img
                          src={imageUrl}
                          alt="Предпросмотр"
                          onError={() => setImageError(true)}
                        />
                      </div>
                    )}
                  </div>
                )}

                <Form.Item
                  name="click_url"
                  label={
                    <Space>
                      <span>Ссылка при клике</span>
                      <InfoCircleOutlined />
                    </Space>
                  }
                  rules={[
                    { type: 'url', message: 'Введите корректный URL (http:// или https://)' },
                  ]}
                  extra="URL, который откроется при клике на уведомление. Работает в WebPush из коробки."
                >
                  <Input
                    placeholder="https://example.com/landing"
                    prefix={<LinkOutlined />}
                  />
                </Form.Item>
                <Form.Item
                  name="custom_data"
                  label={
                    <Space>
                      <span>Дополнительные данные (JSON)</span>
                      <InfoCircleOutlined />
                    </Space>
                  }
                  rules={[{ validator: validatePushSize }]}
                >
                  <TextArea
                    placeholder='{"key": "value"}'
                    rows={3}
                    style={{ fontFamily: 'monospace' }}
                  />
                </Form.Item>
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Кому" className="settings-card">
                <Form.Item>
                  <Segmented
                    value={targetType}
                    onChange={setTargetType}
                    options={[
                      {
                        label: 'Всем пользователям',
                        value: 'all',
                        icon: <TeamOutlined />,
                      },
                      {
                        label: 'Сегмент',
                        value: 'segment',
                        icon: <TeamOutlined />,
                      },
                    ]}
                    block
                  />
                </Form.Item>

                {targetType === 'segment' && (
                  <Form.Item
                    name="segment_id"
                    rules={[
                      { required: true, message: 'Выберите сегмент' },
                    ]}
                  >
                    <Select
                      placeholder="Выберите сегмент"
                      onChange={handleSegmentChange}
                      disabled={!selectedAppId}
                    >
                      {segments.map((segment) => (
                        <Option key={segment.id} value={segment.id}>
                          {segment.name} ({segment.user_count} польз.)
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                <Alert message={reachMessage} type="info" showIcon />
              </Card>
              <Card title="Когда" className="settings-card">
                <Form.Item>
                  <Segmented
                    value={scheduleType}
                    onChange={setScheduleType}
                    options={[
                      { label: 'Сейчас', value: 'now' },
                      {
                        label: 'По расписанию',
                        value: 'later',
                        icon: <ClockCircleOutlined />,
                      },
                    ]}
                    block
                  />
                </Form.Item>

                {scheduleType === 'later' && (
                  <>
                    <Form.Item label="Часовой пояс">
                      <Select
                        value={timezone}
                        onChange={setTimezone}
                        options={timezoneOptions}
                        showSearch
                        optionFilterProp="label"
                        suffixIcon={<GlobalOutlined />}
                      />
                    </Form.Item>

                    <Form.Item
                      name="schedule_at"
                      rules={[
                        {
                          required: true,
                          message: 'Выберите дату и время',
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const iso = buildScheduleISO(value, timezone);
                            if (dayjs(iso).isBefore(dayjs())) {
                              return Promise.reject('Время должно быть в будущем');
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <DatePicker
                        showTime
                        format="YYYY-MM-DD HH:mm"
                        style={{ width: '100%' }}
                        placeholder="Выберите дату и время"
                        showNow={false}
                        renderExtraFooter={() => (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => {
                              form.setFieldsValue({
                                schedule_at: dayjs().tz(timezone).add(10, 'minute'),
                              });
                            }}
                          >
                            Через 10 минут
                          </Button>
                        )}
                        disabledDate={(current) =>
                          current && current < dayjs().tz(timezone).startOf('day')
                        }
                      />
                    </Form.Item>

                    {scheduleAt && timezone !== browserTz && (
                      <AntText type="secondary" style={{ fontSize: 12 }}>
                        {scheduleAt.format('D MMMM, HH:mm')} в {timezone} — это{' '}
                        {dayjs(buildScheduleISO(scheduleAt, timezone))
                          .tz(browserTz)
                          .format('D MMMM, HH:mm')}{' '}
                        по вашему времени
                      </AntText>
                    )}
                  </>
                )}
              </Card>
              <Card title="Проверка" className="settings-card">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input
                    placeholder="Push-токен устройства"
                    value={testToken}
                    onChange={(e) => setTestToken(e.target.value)}
                    prefix={<ExperimentOutlined />}
                    allowClear
                  />
                  <Button
                    onClick={handleTestSend}
                    loading={testSending}
                    disabled={!testToken.trim()}
                    block
                  >
                    Отправить на тестовое устройство
                  </Button>
                  <AntText type="secondary" style={{ fontSize: 12 }}>
                    Уйдёт только на этот токен. Сегмент и расписание не учитываются,
                    в статистику не попадает.
                  </AntText>
                </Space>
              </Card>
              <Card className="actions-card">
                <Space style={{ width: '100%' }} direction="vertical">
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    htmlType="submit"
                    loading={isSending}
                    size="large"
                    block
                  >
                    {scheduleType === 'now'
                      ? 'Отправить сейчас'
                      : 'Запланировать'}
                  </Button>
                  <Button
                    onClick={() => navigate(ROUTES.PUSH_CENTER)}
                    block
                  >
                    Отмена
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </Form>
      </Card>
      <Modal
        open={!!confirmValues}
        title={scheduleType === 'now' ? 'Отправить push?' : 'Запланировать push?'}
        okText={scheduleType === 'now' ? 'Отправить' : 'Запланировать'}
        cancelText="Вернуться к редактированию"
        okButtonProps={{
          danger: scheduleType === 'now' && targetType === 'all',
        }}
        confirmLoading={isSending}
        onOk={handleConfirmedSend}
        onCancel={() => setConfirmValues(null)}
        width={560}
      >
        {confirmValues && (
          <>
            {scheduleType === 'now' && targetType === 'all' && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Уйдёт сразу всем пользователям приложения — ${formatters.number(
                  appReach
                )}. Отменить будет нельзя.`}
              />
            )}

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Приложение">
                {selectedApp
                  ? `${selectedApp.name} (${selectedApp.bundle_id})`
                  : confirmValues.app_id}
              </Descriptions.Item>
              <Descriptions.Item label="Тип">
                {PUSH_TYPE_LABELS[confirmValues.push_type] ?? confirmValues.push_type}
              </Descriptions.Item>
              <Descriptions.Item label="Кому">
                {targetType === 'segment'
                  ? `Сегмент «${confirmSegmentName ?? confirmValues.segment_id}»`
                  : 'Всем пользователям'}
                {` — ${formatters.number(estimatedReach)}`}
              </Descriptions.Item>
              <Descriptions.Item label="Когда">
                {confirmScheduleISO ? (
                  <>
                    {confirmValues.schedule_at.format('D MMMM YYYY, HH:mm')} ({timezone})
                    {timezone !== browserTz && (
                      <>
                        <br />
                        <AntText type="secondary" style={{ fontSize: 12 }}>
                          {dayjs(confirmScheduleISO)
                            .tz(browserTz)
                            .format('D MMMM YYYY, HH:mm')}{' '}
                          по вашему времени
                        </AntText>
                      </>
                    )}
                  </>
                ) : (
                  'Сразу'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Заголовок">
                {confirmValues.title}
              </Descriptions.Item>
              <Descriptions.Item label="Текст">
                {confirmValues.body}
              </Descriptions.Item>
              {confirmValues.click_url && (
                <Descriptions.Item label="Ссылка">
                  {confirmValues.click_url}
                </Descriptions.Item>
              )}
              {confirmValues.image_url && !imageError && (
                <Descriptions.Item label="Изображение">
                  <img
                    src={confirmValues.image_url}
                    alt="Предпросмотр"
                    style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 4 }}
                  />
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </Modal>
    </div>
  );
};

export default SendPush;